import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  GripVertical,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  ArrowLeft,
  Settings,
  Scale,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Types
export interface WorkflowLevel {
  id: string;
  levelNumber: number;
  followUpDays: number;
  escalationDays: number;
  assignedUser: string;
  escalationUser: string;
  documentTemplate: string;
}

export interface WorkflowData {
  workflowName: string;
  description: string;
  levels: WorkflowLevel[];
}

// Mock Data
const mockUsers = [
  'Sarah Johnson',
  'John Smith',
  'Michael Chen',
  'Emma Williams',
  'David Martinez',
];

const mockDocumentTemplates = [
  'Initial Dispute Acknowledgment',
  'Dispute Investigation Letter',
  'Payment Plan Proposal',
  'Final Resolution Notice',
  'Escalation to Management',
];

interface WorkflowEditorProps {
  disputeType?: { id: string; name: string; description: string } | null;
  onSave?: (workflow: WorkflowData) => void;
}

export function WorkflowEditor({ disputeType, onSave }: WorkflowEditorProps) {
  const navigate = useNavigate();
  const [workflowName, setWorkflowName] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState<WorkflowLevel[]>([]);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Generate mock workflow data based on dispute type
  const generateWorkflowData = (disputeTypeName: string) => {
    const workflowConfigs: Record<string, { 
      workflowName: string; 
      description: string; 
      levels: WorkflowLevel[] 
    }> = {
      'Billing Error': {
        workflowName: 'Billing Error Resolution Workflow',
        description: 'Standard workflow for handling billing errors and pricing discrepancies',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'John Smith',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 3,
            escalationDays: 8,
            assignedUser: 'John Smith',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Dispute Investigation Letter',
          },
          {
            id: 'level-3',
            levelNumber: 3,
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Michael Chen',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
      'Service Quality': {
        workflowName: 'Service Quality Dispute Workflow',
        description: 'Workflow for handling service delivery quality and performance issues',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Emma Williams',
            escalationUser: 'David Martinez',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 5,
            escalationDays: 12,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Dispute Investigation Letter',
          },
          {
            id: 'level-3',
            levelNumber: 3,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'John Smith',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Payment Plan Proposal',
          },
          {
            id: 'level-4',
            levelNumber: 4,
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
      'Payment Dispute': {
        workflowName: 'Payment Dispute Resolution Workflow',
        description: 'Fast-track workflow for payment processing and allocation errors',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 1,
            escalationDays: 3,
            assignedUser: 'Michael Chen',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'John Smith',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
      'Contract Terms': {
        workflowName: 'Contract Terms Dispute Workflow',
        description: 'Comprehensive workflow for contract interpretation and terms disagreements',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 5,
            escalationDays: 14,
            assignedUser: 'John Smith',
            escalationUser: 'Emma Williams',
            documentTemplate: 'Dispute Investigation Letter',
          },
          {
            id: 'level-3',
            levelNumber: 3,
            followUpDays: 4,
            escalationDays: 10,
            assignedUser: 'Emma Williams',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Payment Plan Proposal',
          },
          {
            id: 'level-4',
            levelNumber: 4,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Escalation to Management',
          },
          {
            id: 'level-5',
            levelNumber: 5,
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Michael Chen',
            escalationUser: 'David Martinez',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
      'Duplicate Charge': {
        workflowName: 'Duplicate Charge Resolution Workflow',
        description: 'Quick resolution workflow for duplicate invoices and charges',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 1,
            escalationDays: 2,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 1,
            escalationDays: 3,
            assignedUser: 'Michael Chen',
            escalationUser: 'John Smith',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
      'Product Defect': {
        workflowName: 'Product Defect Dispute Workflow',
        description: 'Workflow for handling defective products and goods not as described',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Emma Williams',
            escalationUser: 'David Martinez',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 5,
            escalationDays: 10,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Dispute Investigation Letter',
          },
          {
            id: 'level-3',
            levelNumber: 3,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'John Smith',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Final Resolution Notice',
          },
        ],
      },
    };

    return workflowConfigs[disputeTypeName] || {
      workflowName: `${disputeTypeName} Workflow`,
      description: `Standard workflow for handling ${disputeTypeName.toLowerCase()}`,
      levels: [
        {
          id: 'level-1',
          levelNumber: 1,
          followUpDays: 3,
          escalationDays: 7,
          assignedUser: 'Sarah Johnson',
          escalationUser: 'John Smith',
          documentTemplate: 'Initial Dispute Acknowledgment',
        },
        {
          id: 'level-2',
          levelNumber: 2,
          followUpDays: 5,
          escalationDays: 10,
          assignedUser: 'John Smith',
          escalationUser: 'Michael Chen',
          documentTemplate: 'Dispute Investigation Letter',
        },
      ],
    };
  };

  // Load initial data or set defaults
  useEffect(() => {
    if (disputeType?.name) {
      const workflowData = generateWorkflowData(disputeType.name);
      setWorkflowName(workflowData.workflowName);
      setDescription(workflowData.description);
      setLevels(workflowData.levels);
      setErrors({});
    }
  }, [open, disputeType]);

  // Calculate timeline data
  const timelineData = useMemo(() => {
    let cumulativeFollowUp = 0;
    let cumulativeEscalation = 0;

    const levelTimelines = levels.map((level, index) => {
      if (index === 0) {
        cumulativeFollowUp = level.followUpDays;
        cumulativeEscalation = level.escalationDays;
      } else {
        const prevEscalation = cumulativeEscalation;
        cumulativeFollowUp = prevEscalation + level.followUpDays;
        cumulativeEscalation = prevEscalation + level.escalationDays;
      }

      return {
        levelNumber: level.levelNumber,
        followUpDay: cumulativeFollowUp,
        escalationDay: cumulativeEscalation,
        assignedUser: level.assignedUser,
        escalationUser: level.escalationUser,
        documentTemplate: level.documentTemplate,
      };
    });

    const totalDuration = levelTimelines.length > 0 
      ? levelTimelines[levelTimelines.length - 1].escalationDay 
      : 0;

    return { levelTimelines, totalDuration };
  }, [levels]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!workflowName.trim()) {
      newErrors.workflowName = 'Workflow name is required';
    }

    if (levels.length === 0) {
      newErrors.levels = 'At least one workflow level is required';
    }

    levels.forEach((level, index) => {
      if (level.followUpDays <= 0) {
        newErrors[`level-${index}-followUp`] = 'Follow-up days must be greater than 0';
      }

      if (level.escalationDays <= 0) {
        newErrors[`level-${index}-escalation`] = 'Escalation days must be greater than 0';
      }

      if (level.escalationDays <= level.followUpDays) {
        newErrors[`level-${index}-escalation`] = 'Escalation days must be greater than follow-up days';
      }

      if (!level.assignedUser) {
        newErrors[`level-${index}-user`] = 'Assigned user is required';
      }

      if (!level.escalationUser) {
        newErrors[`level-${index}-escalationUser`] = 'Escalation user is required';
      }

      if (!level.documentTemplate) {
        newErrors[`level-${index}-template`] = 'Document template is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    return (
      workflowName.trim() !== '' &&
      levels.length > 0 &&
      levels.every(
        (level) =>
          level.followUpDays > 0 &&
          level.escalationDays > level.followUpDays &&
          level.assignedUser &&
          level.escalationUser &&
          level.documentTemplate
      )
    );
  };

  // Level management
  const addLevel = () => {
    if (levels.length >= 5) {
      toast.error('Maximum 5 levels reached');
      return;
    }

    const newLevel: WorkflowLevel = {
      id: `level-${Date.now()}`,
      levelNumber: levels.length + 1,
      followUpDays: 0,
      escalationDays: 0,
      assignedUser: '',
      escalationUser: '',
      documentTemplate: '',
    };

    setLevels([...levels, newLevel]);
  };

  const removeLevel = (index: number) => {
    const updatedLevels = levels.filter((_, i) => i !== index);
    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));
    setLevels(renumberedLevels);
  };

  const updateLevel = (index: number, field: keyof WorkflowLevel, value: string | number) => {
    const updatedLevels = [...levels];
    updatedLevels[index] = { ...updatedLevels[index], [field]: value };
    setLevels(updatedLevels);
  };

  const moveLevel = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= levels.length) return;

    const updatedLevels = [...levels];
    const [movedLevel] = updatedLevels.splice(fromIndex, 1);
    updatedLevels.splice(toIndex, 0, movedLevel);

    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));

    setLevels(renumberedLevels);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveLevel(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save handler
  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix all validation errors before saving');
      return;
    }

    const workflowData: WorkflowData = {
      workflowName,
      description,
      levels,
    };

    if (onSave) {
      onSave(workflowData);
    }
    toast.success('Workflow updated successfully');
    navigate('/settings/dispute-types');
  };

  const handleCancel = () => {
    navigate('/settings/dispute-types');
  };

  const breadcrumbItems = [
    { label: 'Settings', href: '/settings', icon: Settings },
    { label: 'Dispute Types', href: '/settings/dispute-types', icon: Scale },
    { label: `Edit Workflow: ${disputeType?.name || 'Unknown'}` },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Header */}
      <div className="border-b bg-background pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleCancel}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                Edit Workflow{disputeType ? `: ${disputeType.name}` : ''}
              </h1>
              <p className="text-sm text-muted-foreground">
                {disputeType?.description || 'Configure workflow settings and escalation levels'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isFormValid()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Update Workflow
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        <div className="container max-w-7xl mx-auto p-6 space-y-6">
          {/* Basic Information Section */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Form Fields */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workflowName">
                      Workflow Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="workflowName"
                      value={workflowName}
                      onChange={(e) => setWorkflowName(e.target.value)}
                      placeholder="Enter workflow name"
                      className={errors.workflowName ? 'border-red-500' : ''}
                    />
                    {errors.workflowName && (
                      <p className="text-sm text-red-500">{errors.workflowName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter workflow description"
                      rows={3}
                    />
                    <p className="text-sm text-muted-foreground">
                      Provide a clear description to help team members understand when to use this workflow.
                    </p>
                  </div>
                </div>

                {/* Right Column - Configuration Tips */}
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Workflow Configuration Tips
                      </p>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                        <li>Escalation days must be greater than follow-up days for each level</li>
                        <li>Drag and drop levels to reorder them</li>
                        <li>You can configure up to 5 escalation levels</li>
                        <li>Each level can be assigned to different users with specific document templates</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Levels Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Workflow Levels</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Define escalation levels with timelines and assignments
                  </p>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={addLevel}
                        disabled={levels.length >= 5}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Level
                      </Button>
                    </TooltipTrigger>
                    {levels.length >= 5 && (
                      <TooltipContent>
                        <p>Maximum 5 levels reached</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {errors.levels && (
                <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.levels}</p>
                </div>
              )}

              {/* Workflow Level Cards */}
              <div className="space-y-3">
                {levels.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <p className="text-muted-foreground mb-4">No workflow levels defined</p>
                      <Button onClick={addLevel} variant="outline" size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Level
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  levels.map((level, index) => (
                    <Card
                      key={level.id}
                      className="border hover:border-teal-300 dark:hover:border-teal-700 transition-colors"
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                          {/* Drag Handle and Level Badge */}
                          <div className="flex items-center gap-2 flex-shrink-0 pt-2">
                            <div className="cursor-grab active:cursor-grabbing">
                              <GripVertical className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <Badge className="bg-teal-600 text-white whitespace-nowrap">
                              Level {level.levelNumber}
                            </Badge>
                          </div>

                          {/* All Fields in a Single Row */}
                          <div className="flex-1 grid grid-cols-5 gap-3">
                            {/* Follow Up Days */}
                            <div className="space-y-1.5">
                              <Label htmlFor={`followUpDays-${index}`} className="text-xs">
                                Follow Up Days <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id={`followUpDays-${index}`}
                                type="number"
                                min="1"
                                value={level.followUpDays || ''}
                                onChange={(e) =>
                                  updateLevel(index, 'followUpDays', parseInt(e.target.value) || 0)
                                }
                                placeholder="Days"
                                className={
                                  errors[`level-${index}-followUp`] ? 'border-red-500 h-9' : 'h-9'
                                }
                              />
                              {errors[`level-${index}-followUp`] && (
                                <p className="text-xs text-red-500">
                                  {errors[`level-${index}-followUp`]}
                                </p>
                              )}
                            </div>

                            {/* Escalation Days */}
                            <div className="space-y-1.5">
                              <Label htmlFor={`escalationDays-${index}`} className="text-xs">
                                Escalation Days <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id={`escalationDays-${index}`}
                                type="number"
                                min="1"
                                value={level.escalationDays || ''}
                                onChange={(e) =>
                                  updateLevel(index, 'escalationDays', parseInt(e.target.value) || 0)
                                }
                                placeholder="Days"
                                className={
                                  errors[`level-${index}-escalation`] ? 'border-red-500 h-9' : 'h-9'
                                }
                              />
                              {errors[`level-${index}-escalation`] && (
                                <p className="text-xs text-red-500">
                                  {errors[`level-${index}-escalation`]}
                                </p>
                              )}
                            </div>

                            {/* Assigned User */}
                            <div className="space-y-1.5">
                              <Label htmlFor={`assignedUser-${index}`} className="text-xs">
                                Assigned User <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={level.assignedUser}
                                onValueChange={(value) => updateLevel(index, 'assignedUser', value)}
                              >
                                <SelectTrigger
                                  className={errors[`level-${index}-user`] ? 'border-red-500 h-9' : 'h-9'}
                                >
                                  <SelectValue placeholder="Select user" />
                                </SelectTrigger>
                                <SelectContent>
                                  {mockUsers.map((user) => (
                                    <SelectItem key={user} value={user}>
                                      {user}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors[`level-${index}-user`] && (
                                <p className="text-xs text-red-500">{errors[`level-${index}-user`]}</p>
                              )}
                            </div>

                            {/* Escalation User */}
                            <div className="space-y-1.5">
                              <Label htmlFor={`escalationUser-${index}`} className="text-xs">
                                Escalation User <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={level.escalationUser}
                                onValueChange={(value) => updateLevel(index, 'escalationUser', value)}
                              >
                                <SelectTrigger
                                  className={errors[`level-${index}-escalationUser`] ? 'border-red-500 h-9' : 'h-9'}
                                >
                                  <SelectValue placeholder="Select user" />
                                </SelectTrigger>
                                <SelectContent>
                                  {mockUsers.map((user) => (
                                    <SelectItem key={user} value={user}>
                                      {user}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors[`level-${index}-escalationUser`] && (
                                <p className="text-xs text-red-500">{errors[`level-${index}-escalationUser`]}</p>
                              )}
                            </div>

                            {/* Document Template */}
                            <div className="space-y-1.5">
                              <Label htmlFor={`documentTemplate-${index}`} className="text-xs">
                                Document Template <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={level.documentTemplate}
                                onValueChange={(value) =>
                                  updateLevel(index, 'documentTemplate', value)
                                }
                              >
                                <SelectTrigger
                                  className={
                                    errors[`level-${index}-template`] ? 'border-red-500 h-9' : 'h-9'
                                  }
                                >
                                  <SelectValue placeholder="Select template" />
                                </SelectTrigger>
                                <SelectContent>
                                  {mockDocumentTemplates.map((template) => (
                                    <SelectItem key={template} value={template}>
                                      {template}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {errors[`level-${index}-template`] && (
                                <p className="text-xs text-red-500">
                                  {errors[`level-${index}-template`]}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Delete Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLevel(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0 h-9 w-9 p-0 mt-6"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workflow Timeline Section */}
          {levels.length > 0 && (
            <Card className="bg-gray-50 dark:bg-gray-900/50">
              <CardHeader>
                <Collapsible open={isTimelineExpanded} onOpenChange={setIsTimelineExpanded}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full hover:opacity-80">
                    <CardTitle className="text-base">Workflow Timeline Preview</CardTitle>
                    {isTimelineExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-muted-foreground mt-1">
                      Visual representation of the complete workflow timeline
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              </CardHeader>
              <Collapsible open={isTimelineExpanded} onOpenChange={setIsTimelineExpanded}>
                <CollapsibleContent>
                  <CardContent className="space-y-3">
                    {timelineData.levelTimelines.map((timeline, index) => (
                      <div
                        key={index}
                        className="p-3 bg-white dark:bg-gray-800 rounded-lg border"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-teal-600 text-white">
                            Level {timeline.levelNumber}
                          </Badge>
                          <span className="text-sm font-medium">
                            Follow-up: Day {timeline.followUpDay} • Escalates: Day{' '}
                            {timeline.escalationDay}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Assigned to: {timeline.assignedUser} • Escalates to: {timeline.escalationUser} • Template:{' '}
                          {timeline.documentTemplate}
                        </p>
                      </div>
                    ))}

                    <div className="pt-2 border-t">
                      <p className="text-sm font-semibold">
                        Total Duration:{' '}
                        <span className="text-teal-600 dark:text-teal-400">
                          Up to {timelineData.totalDuration} days
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}


        </div>
      </div>
    </div>
  );
}
